import React, { useState, useEffect } from 'react';
import './styles/global.css';
import AdminDashboard from './pages/AdminDashboard';
import AvailableLeads from './pages/AvailableLeads';
import MyPipeline from './pages/MyPipeline';
import HotLeads from './pages/HotLeads';
import AutopilotView from './pages/AutopilotView';
import ManagerDashboard from './pages/ManagerDashboard';
import ManagerCommandCenter from './pages/ManagerCommandCenter';
import PlatformSettings from './pages/PlatformSettings';
import BootstrapSuperAdmin from './pages/BootstrapSuperAdmin';
import Impersonate from './pages/Impersonate';
import SetPassword from './pages/SetPassword';
import ContactDetail from './components/ContactDetail';
import { api } from './services/api';
// S5 mobile nav components
import MobileTopNav from './components/MobileTopNav';
import BrandTheme from './components/BrandTheme';
import MobileBottomToolbar from './components/MobileBottomToolbar';
import ContactDisambiguationModal from './components/ContactDisambiguationModal';
import ContactCreateConfirmationModal from './components/ContactCreateConfirmationModal';

// Contact view component for direct URL access
function ContactView() {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadContact = async () => {
      try {
        // Extract contact ID from URL path
        const path = window.location.pathname;
        const match = path.match(/\/contacts\/([a-f0-9-]+)/i);
        if (!match) {
          setError('No contact ID found in URL');
          setLoading(false);
          return;
        }
        const contactId = match[1];
        
        const contactData = await api.getContact(contactId);
        setContact(contactData);
      } catch (err) {
        setError(err.message || 'Failed to load contact');
      } finally {
        setLoading(false);
      }
    };
    loadContact();
  }, []);

  if (loading) return <div style={{ padding: 20, color: '#f1f5f9' }}>Loading contact...</div>;
  if (error) return <div style={{ padding: 20, color: '#ef4444' }}>Error: {error}</div>;
  if (!contact) return <div style={{ padding: 20, color: '#f1f5f9' }}>Contact not found</div>;

  return (
    <div style={{ padding: 20 }}>
      <ContactDetail 
        contact={contact} 
        onClose={() => window.location.href = '/'} 
        onActivityAdded={() => {}}
        onDeleted={() => window.location.href = '/'}
      />
    </div>
  );
}

function App() {
  const [apiStatus, setApiStatus] = useState('checking');
  const [isLoggedIn, setIsLoggedIn] = useState(!!api.token);
  const [accountLocked, setAccountLocked] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [user, setUser] = useState(api.user);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ── S5: add-contact flow state (used by MobileTopNav '+' button) ──────────
  // Mirrors the same modal state that MyPipeline uses for the voice flow.
  // The '+' tap pre-fills nothing and lands straight on the confirm modal with
  // an empty proposed contact, letting the user type name/phone manually.
  const [mobileAddDisambigOpen, setMobileAddDisambigOpen] = useState(false);
  const [mobileAddDisambigMatches, setMobileAddDisambigMatches] = useState([]);
  const [mobileAddConfirmOpen, setMobileAddConfirmOpen] = useState(false);
  const [mobileAddProposed, setMobileAddProposed] = useState(null);

  /** Called when the '+' icon in MobileTopNav is tapped. Opens the
   *  ContactCreateConfirmationModal with a blank proposed contact so the user
   *  can fill in details and confirm. */
  const handleMobileAddContact = () => {
    setMobileAddProposed({ first_name: '', last_name: '', phone: '', email: '' });
    setMobileAddConfirmOpen(true);
  };

  const handleMobileAddConfirm = async () => {
    if (!mobileAddProposed) return;
    try {
      await api.confirmCreateContact(mobileAddProposed);
    } catch (e) {
      console.error('MobileTopNav: create contact failed', e);
    } finally {
      setMobileAddConfirmOpen(false);
      setMobileAddProposed(null);
    }
  };

  const handleMobileAddCancel = () => {
    setMobileAddConfirmOpen(false);
    setMobileAddProposed(null);
  };
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const onLocked = () => {
      setAccountLocked(true);
      setIsLoggedIn(false);
    };
    window.addEventListener('vinnie:account_locked', onLocked);
    return () => window.removeEventListener('vinnie:account_locked', onLocked);
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      checkApiHealth();
      loadUser();
      // Check if URL is a contact deep-link
      const path = window.location.pathname;
      if (path.match(/\/contacts\/([a-f0-9-]+)/i)) {
        setCurrentView('contact');
      }
    }
  }, [isLoggedIn]);

  const loadUser = async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      api.setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
    }
  };

  const checkApiHealth = async () => {
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${base}/health`);
      if (response.ok) {
        setApiStatus('online');
      } else {
        setApiStatus('offline');
      }
    } catch (error) {
      setApiStatus('offline');
    }
  };


  const handleLogout = () => {
    api.clearToken();
    setIsLoggedIn(false);
    setUser(null);
    setCurrentView('home');
    setMobileMenuOpen(false);
  };

  const handleNavClick = (viewId) => {
    setCurrentView(viewId);
    setMobileMenuOpen(false);
  };

  // Navigation items based on user role
  const getNavItems = () => {
    const items = [
      { id: 'home', label: 'Hot Leads', shortLabel: 'Hot', roles: ['admin', 'manager', 'salesperson', 'super_admin'] },
      { id: 'pipeline', label: 'My Pipeline', shortLabel: 'Pipeline', roles: ['admin', 'manager', 'salesperson', 'super_admin'] },
      // Autopilot: all roles see it, but scope differs — salesperson sees only
      // their own claimed leads' messaging (my-autopilot-jobs), manager/admin/
      // super_admin see the global dealer-wide view (admin/autopilot/jobs).
      // The AutopilotView component itself decides which API to call based
      // on isGlobal (see renderContent below).
      { id: 'autopilot', label: 'Autopilot', shortLabel: 'Autopilot', roles: ['admin', 'manager', 'salesperson', 'super_admin'] },
      // Available (cold-lead triage pool) is manager+ only. Salespeople's claim
      // pool is Hot Leads only — see backend require_manager gate on
      // GET /leads/available (leads.py get_available_leads). Previously this
      // nav item incorrectly listed 'salesperson' in roles, which let
      // salespeople click through to a page whose API call would 403.
      { id: 'available', label: 'Available', shortLabel: 'Available', roles: ['admin', 'manager', 'super_admin'] },
    ];

    if (user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin') {
      items.push({ id: 'command_centre', label: 'Command Centre', shortLabel: 'CC', roles: ['admin', 'manager', 'super_admin'] });
    }

    items.push({ id: 'admin', label: 'Admin', shortLabel: 'Admin', roles: ['admin', 'manager', 'super_admin'] });
    if (user?.role === 'super_admin') {
      items.push({ id: 'platform', label: 'Platform', shortLabel: 'Platform', roles: ['super_admin'] });
    }

    return items.filter(item => item.roles.includes(user?.role));
  };

  const renderContent = () => {
    switch (currentView) {
      case 'admin':
      case 'admin_profile':
        return <AdminDashboard initialTab="profile" />;
      case 'admin_knowledge':
        return <AdminDashboard initialTab="knowledge" />;
      case 'admin_staff':
        return <AdminDashboard initialTab="staff" />;

      case 'admin_dms':
        return <AdminDashboard initialTab="dms" />;
      case 'admin_sites':
        return <AdminDashboard initialTab="sites" />;
      case 'admin_dealer_groups':
        return <AdminDashboard initialTab="dealer_groups" />;
      case 'admin_comms_setup':
        return <AdminDashboard initialTab="comms_setup" />;
      case 'admin_email_suppression':
        return <AdminDashboard initialTab="email_suppression" />;
      case 'admin_advanced_comms':
        return <AdminDashboard initialTab="advanced_comms" />;
      case 'available':
        return <AvailableLeads />;
      case 'autopilot':
        return (
          <AutopilotView
            isGlobal={['manager', 'admin', 'super_admin'].includes(user?.role)}
          />
        );
      case 'pipeline':
        return <MyPipeline />;
      case 'manager':
        return <ManagerDashboard />;
      case 'command_centre':
        return <ManagerCommandCenter />;
      case 'platform':
        return <PlatformSettings />;
      case 'contact':
        return <ContactView />;
      case 'home':
      default:
        return <HotLeads />;
    }
  };

  const isBootstrapPage = window.location.pathname === '/bootstrap-super-admin';
  const isSetPasswordPage = window.location.pathname === '/set-password';

  if (isSetPasswordPage) {
    return <SetPassword />;
  }

  if (isBootstrapPage) {
    return <BootstrapSuperAdmin />;
  }

  const isImpersonatePage = window.location.pathname === '/impersonate';
  if (isImpersonatePage) {
    return <Impersonate />;
  }

  if (accountLocked) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
        <div style={{ background: '#1e293b', border: '1px solid #ef4444', borderRadius: '12px', padding: '40px 48px', maxWidth: '480px', textAlign: 'center' }}>
          <div style={{ color: '#ef4444', fontSize: '18px', fontWeight: '700', marginBottom: '12px', fontFamily: "'Noto Sans', sans-serif" }}>Account Suspended</div>
          <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6', margin: '0 0 24px 0', fontFamily: "'Noto Sans', sans-serif" }}>
            Your Vinnie account has been suspended due to a payment issue. Please contact your account manager or email support to restore access.
          </p>
          <div style={{ color: '#64748b', fontSize: '12px', fontFamily: "'Noto Sans', sans-serif" }}>support@vinnie.autos</div>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <LoginScreen onLogin={() => setIsLoggedIn(true)} />;
  }

  const navItems = getNavItems();

  // Desktop sidebar groups
  const salesNav = navItems.filter(i => ['home', 'pipeline', 'autopilot', 'available'].includes(i.id));
  const managementNav = navItems.filter(i => ['manager', 'command_centre'].includes(i.id));
  const adminNav = navItems.filter(i => i.id === 'admin');
  const platformNav = navItems.filter(i => i.id === 'platform');

  // Admin sub-navigation (desktop sidebar)
  const adminSubItems = [
    { id: 'admin_profile', label: 'Dealer Group Profile', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'admin_knowledge', label: 'Knowledge', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'admin_staff', label: 'Users', roles: ['admin', 'manager', 'super_admin'] },

    { id: 'admin_dms', label: 'DMS', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'admin_sites', label: 'Sites', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'admin_dealer_groups', label: 'Dealer Groups', roles: ['super_admin'] },
    { id: 'admin_comms_setup', label: 'Comms Setup', roles: ['super_admin'] },
    { id: 'admin_advanced_comms', label: 'Advanced Comms', roles: ['admin', 'manager', 'super_admin'] },
    { id: 'admin_email_suppression', label: 'Email Suppression', roles: ['admin', 'super_admin'] },
  ].filter(item => item.roles.includes(user?.role));

  return (
    <div style={styles.container}>
      <BrandTheme />
      {/* Header */}
      {(() => {
        try {
          const meta = JSON.parse(localStorage.getItem('impersonation_meta') || 'null');
          if (meta && meta.impersonation) {
            return (
              <div style={{ background: '#fff3cd', color: '#664d03', padding: '8px 16px', fontSize: 14 }}>
                <strong>Impersonating tenant</strong>
                {meta.dealer_id ? ` (dealer_id: ${meta.dealer_id})` : ''}
                <button
                  onClick={() => {
                    localStorage.removeItem('impersonation_meta');
                    api.clearToken();
                    window.location.href = '/';
                  }}
                  style={{ marginLeft: 12, padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}
                >
                  Exit impersonation
                </button>
              </div>
            );
          }
        } catch (e) {}
        return null;
      })()}
      {/* S5: Mobile Top Nav — shown only on mobile; replaces the hamburger menu header */}
      {/* The existing <header> below remains for desktop. */}
      <div className="mobile-only">
        <MobileTopNav onAddContact={handleMobileAddContact} onLogout={handleLogout} />
      </div>

      {/* S5: add-contact modals (wired to MobileTopNav '+' button) */}
      <ContactDisambiguationModal
        open={mobileAddDisambigOpen}
        matches={mobileAddDisambigMatches}
        title="Multiple contacts matched"
        subtitle="Select the existing contact or create a new one."
        onSelect={(m) => {
          setMobileAddDisambigOpen(false);
          setMobileAddDisambigMatches([]);
          // Navigate to their contact detail
          handleNavClick('contact');
        }}
        onClose={() => {
          setMobileAddDisambigOpen(false);
          setMobileAddDisambigMatches([]);
        }}
      />
      <ContactCreateConfirmationModal
        open={mobileAddConfirmOpen}
        proposed={mobileAddProposed}
        onConfirm={handleMobileAddConfirm}
        onCancel={handleMobileAddCancel}
      />

      <header style={styles.header} className="desktop-only">
        <div style={styles.headerLeft}>
          <img src="/vinnie-logo.png" alt="Vinnie" style={{ height: '32px', width: 'auto', display: 'block' }} />
          <span style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: '20px',
            fontWeight: 700,
            color: '#f1f5f9',
            letterSpacing: '-0.3px',
            lineHeight: 1,
            marginLeft: '10px',
          }}>
            Vinnie<span style={{ color: 'var(--brand-primary, #317eeb)' }}>.Autos</span>
          </span>
        </div>

        {/* Mobile Menu Button */}
        <button 
          style={styles.mobileMenuButton}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="mobile-only"
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>

        <div style={styles.headerRight} className="desktop-only">
          <div style={styles.status}>
            <span style={{
              ...styles.statusDot,
              backgroundColor: apiStatus === 'online' ? '#4CAF50' : '#f44336'
            }} />
            {apiStatus === 'online' ? 'Connected' : 'Offline'}
          </div>
          <div style={styles.userInfo}>
            <span style={styles.userName}>{user?.full_name || user?.email}</span>
            <span style={styles.userRole}>{user?.role}</span>
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn}>Logout</button>
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div style={styles.mobileDrawer} className="mobile-only">
          <div style={styles.mobileDrawerHeader}>
            <span style={styles.mobileUserName}>{user?.full_name || user?.email}</span>
            <span style={styles.mobileUserRole}>{user?.role}</span>
          </div>
          
          <nav style={styles.mobileNav}>
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                style={{
                  ...styles.mobileNavButton,
                  ...(currentView === item.id ? styles.mobileNavButtonActive : {}),
                }}
              >
                <span style={styles.mobileNavLabel}>{item.label}</span>
              </button>
            ))}
          </nav>
          
          <div style={styles.mobileDrawerFooter}>
            <div style={styles.mobileStatus}>
              <span style={{
                ...styles.statusDot,
                backgroundColor: apiStatus === 'online' ? '#4CAF50' : '#f44336'
              }} />
              {apiStatus === 'online' ? 'Connected' : 'Offline'}
            </div>
            <button onClick={handleLogout} style={styles.mobileLogoutBtn}>Logout</button>
          </div>
        </div>
      )}

      {/* S5: Mobile Bottom Toolbar — replaces old generic mobile bottom nav */}
      <div className="mobile-only">
        <MobileBottomToolbar currentView={currentView} onNav={handleNavClick} />
      </div>

      {/* Desktop Sidebar + Main Content */}
      <div style={styles.appBody}>
        <aside style={styles.sidebar} className="desktop-only">
          <div style={styles.sidebarSection}>
            <div style={styles.sidebarSectionTitle}>Sales</div>
            {salesNav.map(item => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                style={{
                  ...styles.sidebarButton,
                  ...(currentView === item.id ? styles.sidebarButtonActive : {}),
                }}
              >
                <span style={styles.sidebarButtonLabel}>{item.label}</span>
              </button>
            ))}
          </div>

          {managementNav.length > 0 && (
            <div style={styles.sidebarSection}>
              <div style={styles.sidebarSectionTitle}>Management</div>
              {managementNav.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  style={{
                    ...styles.sidebarButton,
                    ...(currentView === item.id ? styles.sidebarButtonActive : {}),
                  }}
                >
                  <span style={styles.sidebarButtonLabel}>{item.label}</span>
                </button>
              ))}
            </div>
          )}

          {adminNav.length > 0 && (
            <div style={styles.sidebarSection}>
              <div style={styles.sidebarSectionTitle}>Admin</div>
              {adminNav.map(item => (
                <React.Fragment key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.id)}
                    style={{
                      ...styles.sidebarButton,
                      ...(currentView === item.id || currentView.startsWith('admin_') ? styles.sidebarButtonActive : {}),
                    }}
                  >
                    <span style={styles.sidebarButtonLabel}>{item.label}</span>
                  </button>

                  {(currentView === 'admin' || currentView.startsWith('admin_')) && (
                    <div style={{ marginTop: 6, marginLeft: 28 }}>
                      {adminSubItems.map(sub => (
                        <button
                          key={sub.id}
                          onClick={() => handleNavClick(sub.id)}
                          style={{
                            ...styles.sidebarSubButton,
                            ...(currentView === sub.id ? styles.sidebarSubButtonActive : {}),
                          }}
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {platformNav.length > 0 && (
            <div style={styles.sidebarSection}>
              <div style={styles.sidebarSectionTitle}>Platform</div>
              {platformNav.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  style={{
                    ...styles.sidebarButton,
                    ...(currentView === item.id ? styles.sidebarButtonActive : {}),
                  }}
                >
                  <span style={styles.sidebarButtonLabel}>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </aside>

        <main style={styles.main}>
          {renderContent()}
        </main>
      </div>

      {/* Mobile Styles */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-only { display: none !important; }
        }
        @media (min-width: 769px) {
          .mobile-only { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // One-time banner set by /set-password after a successful password set.
    try {
      const msg = sessionStorage.getItem('flash_login_success');
      if (msg) {
        setFlash(msg);
        sessionStorage.removeItem('flash_login_success');
      }
    } catch (e) {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      if (isRegistering) {
        await api.register(email, password, fullName);
        await api.login(email, password);
      } else {
        await api.login(email, password);
      }
      onLogin();
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={loginStyles.container}>
      <div style={loginStyles.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <img src="/vinnie-logo.png" alt="Vinnie" style={{ height: '48px', width: 'auto', flexShrink: 0 }} />
          <span style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: '28px',
            fontWeight: 700,
            color: '#f1f5f9',
            letterSpacing: '-0.5px',
            lineHeight: 1,
          }}>
            Vinnie<span style={{ color: 'var(--brand-primary, #317eeb)' }}>.Autos</span>
          </span>
        </div>

        <p style={loginStyles.subtitle}>
          {isRegistering ? 'Create your account' : 'Sign in to continue'}
        </p>

        {flash && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.2)',
            color: '#86efac',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
          }}>
            {flash}
          </div>
        )}

        {error && <div style={loginStyles.error}>{error}</div>}
        
        <form onSubmit={handleSubmit} style={loginStyles.form}>
          {isRegistering && (
            <input
              type="text"
              placeholder="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={loginStyles.input}
              required
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={loginStyles.input}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={loginStyles.input}
            required
          />
          <button type="submit" style={loginStyles.button} disabled={isLoading}>
            {isLoading ? 'Please wait...' : (isRegistering ? 'Register' : 'Login')}
          </button>
        </form>
        
        <p style={loginStyles.switchText}>
          {isRegistering ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button 
            onClick={() => setIsRegistering(!isRegistering)}
            style={loginStyles.switchBtn}
          >
            {isRegistering ? 'Login' : 'Register'}
          </button>
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0e1525',
    backgroundImage: [
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='400' height='400' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E\")",
      'radial-gradient(ellipse 90% 70% at 15% 5%, rgba(49,126,235,0.13) 0%, transparent 55%)',
      'radial-gradient(ellipse 70% 60% at 85% 90%, rgba(126,87,194,0.09) 0%, transparent 50%)',
      'radial-gradient(ellipse 50% 40% at 55% 45%, rgba(41,182,246,0.05) 0%, transparent 45%)',
      'linear-gradient(160deg, #0e1525 0%, #111d35 50%, #0e1525 100%)',
    ].join(', '),
    backgroundAttachment: 'fixed',
    color: '#f1f5f9',
    paddingBottom: '80px',
  },
  appBody: {
    display: 'flex',
    alignItems: 'stretch',
    minHeight: 'calc(100vh - 56px)',
  },
  sidebar: {
    width: '240px',
    minWidth: '240px',
    background: 'rgba(14,21,37,0.75)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRight: '1px solid rgba(44,53,78,0.6)',
    padding: '12px',
  },
  sidebarSection: {
    marginBottom: '14px',
  },
  sidebarSectionTitle: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#64748b',
    margin: '10px 8px 6px',
    fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  sidebarButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 10px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    borderRadius: '10px',
    color: '#cbd5e1',
    fontSize: '13px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  sidebarButtonActive: {
    background: '#0b2a5a',
    border: '1px solid #1d4ed8',
    color: '#fff',
  },
  sidebarButtonIcon: {
    width: '20px',
    display: 'inline-flex',
    justifyContent: 'center',
    opacity: 0.95,
  },
  sidebarButtonLabel: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sidebarSubButton: {
    width: '100%',
    padding: '8px 10px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    borderRadius: '10px',
    color: '#94a3b8',
    fontSize: '13px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  sidebarSubButtonActive: {
    background: '#0f1f3d',
    border: '1px solid #1d4ed8',
    color: '#fff',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    background: 'rgba(14,21,37,0.90)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(44,53,78,0.6)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#f1f5f9',
    margin: 0,
    whiteSpace: 'nowrap',
  },
  nav: {
    display: 'flex',
    gap: '4px',
  },
  navButton: {
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: '#94a3b8',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  navButtonActive: {
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
  },
  mobileMenuButton: {
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    color: '#f1f5f9',
    fontSize: '20px',
    cursor: 'pointer',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#94a3b8',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  userName: {
    color: '#f1f5f9',
    fontSize: '13px',
    fontWeight: '500',
  },
  userRole: {
    color: '#64748b',
    fontSize: '11px',
    textTransform: 'uppercase',
  },
  logoutBtn: {
    padding: '8px 16px',
    fontSize: '13px',
    background: '#334155',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#f1f5f9',
  },
  mobileDrawer: {
    position: 'fixed',
    top: '57px',
    left: 0,
    right: 0,
    bottom: 0,
    background: '#1e293b',
    zIndex: 99,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
  },
  mobileDrawerHeader: {
    paddingBottom: '20px',
    borderBottom: '1px solid #334155',
    marginBottom: '20px',
  },
  mobileUserName: {
    color: '#f1f5f9',
    fontSize: '16px',
    fontWeight: '500',
    display: 'block',
  },
  mobileUserRole: {
    color: '#64748b',
    fontSize: '12px',
    textTransform: 'uppercase',
  },
  mobileNav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
  },
  mobileNavButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '16px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  mobileNavButtonActive: {
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
  },
  mobileNavIcon: {
    fontSize: '20px',
  },
  mobileNavLabel: {
    fontWeight: '500',
  },
  mobileDrawerFooter: {
    paddingTop: '20px',
    borderTop: '1px solid #334155',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mobileStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#94a3b8',
  },
  mobileLogoutBtn: {
    padding: '12px 24px',
    fontSize: '14px',
    background: '#ef4444',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#fff',
    fontWeight: '600',
  },
  mobileBottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: '#1e293b',
    borderTop: '1px solid #334155',
    display: 'flex',
    justifyContent: 'space-around',
    padding: '8px 0',
    zIndex: 100,
  },
  mobileBottomButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    color: '#94a3b8',
    fontSize: '11px',
    cursor: 'pointer',
    flex: 1,
  },
  mobileBottomButtonActive: {
    color: 'var(--brand-primary, #3b82f6)',
  },
  mobileBottomIcon: {
    fontSize: '20px',
  },
  mobileBottomLabel: {
    fontSize: '10px',
  },
  main: {
    flex: 1,
    padding: '16px',
    overflow: 'auto',
    background: 'transparent',
  },
  errorBanner: {
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#fca5a5',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#fca5a5',
  },
  recorderSection: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  quickActions: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#f1f5f9',
    margin: 0,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  commandList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  },
  command: {
    background: '#1e293b',
    color: '#60a5fa',
    padding: '8px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontStyle: 'italic',
    border: '1px solid #334155',
  },
  contactsSection: {
    flex: 1,
  },
  refreshBtn: {
    padding: '8px 16px',
    fontSize: '13px',
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '44px',
    minWidth: '44px',
  },
};

const loginStyles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f172a',
    padding: '16px',
  },
  card: {
    background: '#1e293b',
    padding: '32px 24px',
    borderRadius: '12px',
    border: '1px solid #334155',
    width: '100%',
    maxWidth: '360px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#f1f5f9',
    margin: '0 0 8px 0',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: '0 0 24px 0',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  input: {
    padding: '14px 16px',
    fontSize: '16px',
    border: '1px solid #334155',
    borderRadius: '8px',
    outline: 'none',
    background: '#0f172a',
    color: '#f1f5f9',
    minHeight: '48px',
  },
  button: {
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '8px',
    minHeight: '48px',
  },
  error: {
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#fca5a5',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  switchText: {
    textAlign: 'center',
    marginTop: '20px',
    fontSize: '14px',
    color: '#94a3b8',
  },
  switchBtn: {
    background: 'none',
    border: 'none',
    color: '#60a5fa',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

export default App;
